import type { AgentFocus } from "@/types/agentFocus.type";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Matches the backend's focusLabel so the chip and the transcript agree. */
export function focusLabel(focus: AgentFocus, short = false): string {
  switch (focus.surface) {
    case "universe":
      return short ? "Universe" : "Brand Universe";
    case "strategy":
      return "Strategy";
    case "calendar": {
      if (focus.year === undefined || focus.month === undefined) return "Calendar";
      const months = short ? SHORT_MONTHS : MONTH_NAMES;
      return `Calendar · ${months[focus.month - 1]} ${focus.year}`;
    }
    case "signals":
      return short ? "Signals" : "Market Signals";
    case "trends":
      return "Trends";
    case "references":
      return "References";
    case "home":
      return "Home";
  }
}

/**
 * True when two messages were sent from meaningfully different places, so the
 * transcript draws a divider. Same comparison the backend replay uses, or the
 * agent and the user would disagree about where a jump happened.
 */
export function focusChanged(a?: AgentFocus, b?: AgentFocus): boolean {
  if (!a || !b) return false;
  if (a.surface !== b.surface) return true;
  if (a.surface === "strategy" && b.surface === "strategy") {
    return a.strategyId !== b.strategyId;
  }
  if (a.surface === "calendar" && b.surface === "calendar") {
    return a.year !== b.year || a.month !== b.month;
  }
  return false;
}

/** Composer placeholder for the surface the user is on. */
export function focusPlaceholder(focus: AgentFocus, monthPlanned: boolean): string {
  switch (focus.surface) {
    case "universe":
      return "Ask about the brand…";
    case "strategy":
      return focus.strategyId ? "Ask about this strategy…" : "Ask about the strategy…";
    case "calendar": {
      if (focus.year === undefined || focus.month === undefined) {
        return "Ask about the calendar…";
      }
      const label = `${MONTH_NAMES[focus.month - 1]} ${focus.year}`;
      return monthPlanned ? `Ask about ${label}…` : `Tell the agent what ${label} needs…`;
    }
    default:
      return "Ask the agent…";
  }
}

/** Three things worth asking on this surface, shown on an empty chat. */
export function focusSuggestions(focus: AgentFocus, monthPlanned: boolean): string[] {
  switch (focus.surface) {
    case "universe":
      return [
        "What is thin in our brand universe?",
        "Seed the universe from our documents",
        "Propose facts about our main competitor",
      ];
    case "strategy":
      return [
        "What does this strategy say so far?",
        "Draft the research section",
        "Settle our monthly posting volume",
      ];
    case "calendar":
      if (focus.year === undefined) {
        return [
          "Which months still need planning?",
          "What is waiting on the client?",
          "Show me what is stuck",
        ];
      }
      return monthPlanned
        ? [
            "What is stuck this month?",
            "What is waiting on the client?",
            "Which pillars are underweight this month?",
          ]
        : [
            "Plan this month",
            "What should this month be about?",
            "What did we post last month?",
          ];
    default:
      return [
        "What needs me today?",
        "Where should I start?",
        "Take me to this month's calendar",
      ];
  }
}
